import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI, authAPI } from '../../helpers/api_service';
import { APP_URLS } from '../../helpers/url_helper';
import { AlertDialog } from '../Common';
import sessionManager from '../../helpers/session_manager';
import { handleOAuthLogout, detectOAuthProvider, getOAuthAccessToken, clearAllOAuthData } from '../../helpers/oauth_logout';
import '../Subscription/Subscription.css';
import './newSubscription.css';

// Import SVG assets for plan icons
import GiftBox from '../../assets/gift-box 1.svg';
import StarterPlan from '../../assets/pricing-starter.svg';
import StandardPlan from '../../assets/pricing-standard.svg';
import PremiumPlan from '../../assets/pricing-premium.svg';
import Checkmark from '../../assets/checkmark.svg';
// Import new plan icons
import Gift1 from '../../assets/gift 1.svg';
import Crown from '../../assets/Crown.svg';
import Rocket1 from '../../assets/rocket 1.svg';
// Import logo for header
import LogoSvg from '../../assets/logo.svg';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getProfileCompanyPicUrl } from '../../helpers/url_helper';

// Helper function to handle profile picture URL
const getProfilePicUrl = (profilePic) => {
  if (!profilePic) return '';
  
  // Check if URL already starts with http (any protocol)
  if (profilePic.toLowerCase().startsWith('http://') || profilePic.toLowerCase().startsWith('https://')) {
    return profilePic; // Return as is if it's already a full URL
  } else {
    return getProfileCompanyPicUrl(profilePic); // Append profile/company pic URL prefix if it's a relative path
  }
};

function NewSubscription() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingToggle, setBillingToggle] = useState('MONTHLY');
  const [userEmail, setUserEmail] = useState('');
  const [userFullName, setUserFullName] = useState('');
  const [userProfilePic, setUserProfilePic] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [expiredSubscription, setExpiredSubscription] = useState(null);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);

  // Initialize theme on component mount
  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(mode === 'dark' ? 'dark-mode' : 'light-mode');
  }, [mode]);

  // Fetch subscription plans with trial parameter
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await dataAPI.getSubscriptionPlans(true); // include_trial = true
        if (response.status === 'success' && response.data) {
          setPlans(response.data);
        } else {
          setError('Failed to load subscription plans');
        }
      } catch (err) {
        console.error('Error fetching subscription plans:', err);
        setError('Failed to load subscription plans');
      } finally {
        setLoading(false);
      }
    };

    const getUserData = () => {
      // Get user data from localStorage (same as DashboardLayout)
      const fullName = localStorage.getItem('userFullName') || '';
      const profilePic = localStorage.getItem('userProfilePic') || '';
      const userData = localStorage.getItem('userData');
      
      setUserFullName(fullName);
      setUserProfilePic(profilePic);
      
      if (userData) {
        try {
          const user = JSON.parse(userData);
          setUserEmail(user.email || user.user_email || '');
          
          // Check for expired subscription
          if (user.subscription_package && user.subscription_package.status === 'expired') {
            setExpiredSubscription(user.subscription_package);
          }
        } catch (err) {
          console.error('Error parsing user data:', err);
        }
      }
    };

    fetchPlans();
    getUserData();
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the options menu
      const optionsMenu = event.target.closest('[data-options-menu]');
      const optionsButton = event.target.closest('[data-options-button]');
      
      if (showOptionsMenu && !optionsMenu && !optionsButton) {
        setShowOptionsMenu(false);
      }
    };

    if (showOptionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsMenu]);

  // Helper function to get plan icon based on plan title
  const getPlanIcon = (planTitle) => {
    const title = planTitle.toLowerCase();
    if (title.includes('trial')) {
      return <img src={GiftBox} className="plan-icon" alt={`${planTitle} Plan`} />;
    } else if (title.includes('standard')) {
      return <img src={Gift1} className="plan-icon" alt={`${planTitle} Plan`} />;
    } else if (title.includes('gold')) {
      return <img src={Crown} className="plan-icon" alt={`${planTitle} Plan`} />;
    } else if (title.includes('platinum')) {
      return <img src={Rocket1} className="plan-icon" alt={`${planTitle} Plan`} />;
    }
    // Default fallback
    return <img src={GiftBox} className="plan-icon" alt={`${planTitle} Plan`} />;
  };

  const isEnterprisePlan = (plan) => {
    if (!plan) return false;
    return plan.is_enterprise || (plan.title && plan.title.toLowerCase().includes('enterprise'));
  };

  // Helper function to format price display
  const formatPrice = (plan) => {
    if (isEnterprisePlan(plan)) {
      return {
        oldPrice: '',
        newPrice: 'Custom pricing',
        period: ''
      };
    }

    if (!plan.month_price) {
      return {
        oldPrice: '',
        newPrice: 'Free',
        period: '/Trial'
      };
    }
    
    if (billingToggle === 'YEARLY') {
      return {
        oldPrice: '',
        newPrice: `$${plan.year_price}`,
        period: '/Yearly'
      };
    }
    
    return {
      oldPrice: '',
      newPrice: `$${plan.month_price}`,
      period: '/Monthly'
    };
  };

  // Helper function to format features from description
  const formatFeatures = (formattedDescription) => {
    if (!formattedDescription) return [];
    
    return formattedDescription.split('\n').map((line, index) => {
      // Skip the first line as it's the title which we're using separately
      if (index === 0) return null;
      
      return (
        <li key={index} className="feature-item">
          <img src={Checkmark} className="feature-check" alt="check" />
          <span>{line}</span>
        </li>
      );
    }).filter(Boolean);
  };

  // Get plan description (first line of formatted_description)
  const getPlanDescription = (formattedDescription) => {
    if (!formattedDescription) return '';
    const lines = formattedDescription.split('\n');
    return lines[0] || '';
  };

  // Check if plan is most popular (highlight only Gold plan)
  const isMostPopular = (plan, index, totalPlans) => {
    const title = plan.title.toLowerCase();
    // Only highlight Gold plan
    return title.includes('gold');
  };

  // Handle plan selection and navigate to checkout
  const handlePlanSelection = (plan, formattedDescription = '') => {
    const enterprisePlan = isEnterprisePlan(plan);

    if (enterprisePlan) {
      const priceInfo = formatPrice(plan);
      navigate(APP_URLS.STANDALONE_CONTACT_SALES, {
        state: {
          planTitle: `${plan.title} Plan`,
          billingCycle: billingToggle,
          displayPrice: `${priceInfo.newPrice}${priceInfo.period}`,
          formattedDescription,
          monthPrice: plan.month_price || null,
          yearPrice: plan.year_price || null,
        }
      });
      return;
    }

    // Handle free trial plan - show confirmation dialog
    if (!plan.month_price) {
      setSelectedPlan({ plan, formattedDescription });
      setShowConfirmDialog(true);
      return;
    }

    // Prepare plan details for checkout
    const planDetails = {
      planId: plan.id,
      planTitle: plan.title,
      billingCycle: billingToggle.toLowerCase(),
      customerEmail: userEmail,
      displayPrice: formatPrice(plan).newPrice + formatPrice(plan).period,
      formattedDescription: formattedDescription
    };

    // Navigate to standalone checkout session with plan details (no sidebar)
    navigate('/standalone-checkout', { state: planDetails });
  };

  // Handle trial plan confirmation
  const handleTrialConfirmation = async () => {
    if (!selectedPlan) return;
    
    try {
      setLoading(true);
      setShowConfirmDialog(false);
      
      const response = await dataAPI.createSubscription({
        subscription_plan_id: selectedPlan.plan.id
      });
      
      if (response.status === 'success') {
        // Redirect to dashboard on success
        navigate(APP_URLS.DASHBOARD);
      } else {
        setError(response.message || 'Failed to create subscription. Please try again.');
      }
    } catch (err) {
      console.error('Error creating subscription:', err);
      setError('Failed to create subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      // Close the options menu first
      setShowOptionsMenu(false);
      
      // Detect OAuth provider and handle OAuth logout first
      const oauthProvider = detectOAuthProvider();
      if (oauthProvider) {
        const accessToken = getOAuthAccessToken(oauthProvider);
        await handleOAuthLogout(oauthProvider, accessToken);
      }
      
      // Then handle backend logout
      await authAPI.logout();
      
      // Clear session and local storage
      sessionManager.clearSession();
      localStorage.removeItem('userData');
      localStorage.removeItem('userFullName');
      localStorage.removeItem('userProfilePic');
      clearAllOAuthData();
      
      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local data and redirect
      sessionManager.clearSession();
      localStorage.removeItem('userData');
      localStorage.removeItem('userFullName');
      localStorage.removeItem('userProfilePic');
      clearAllOAuthData();
      navigate('/login');
    }
  };

  // Helper function to sort plans in correct order: Trial, Standard, Gold, Platinum
  const sortPlans = (plans) => {
    const planOrder = ['trial', 'standard', 'gold', 'platinum'];
    return [...plans].sort((a, b) => {
      const titleA = a.plan.title.toLowerCase();
      const titleB = b.plan.title.toLowerCase();
      
      const indexA = planOrder.findIndex(order => titleA.includes(order));
      const indexB = planOrder.findIndex(order => titleB.includes(order));
      
      // If both found, sort by index
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only one found, put it first
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      // If neither found, maintain original order
      return 0;
    });
  };

  // Separate free plan from paid plans
  const freePlan = plans.find(planData => 
    !planData.plan.month_price || 
    planData.plan.title.toLowerCase().includes('trial') || 
    planData.plan.title.toLowerCase().includes('free')
  );
  
  const paidPlans = plans.filter(planData => 
    planData.plan.month_price && 
    !planData.plan.title.toLowerCase().includes('trial') && 
    !planData.plan.title.toLowerCase().includes('free')
  );

  if (loading) {
    return (
      <div className="subscription-page">
        <Container className="text-center py-5">
          <h1 className="subscription-title">Choose Your Perfect Plan</h1>
          <p className="subscription-subtitle">Loading subscription plans...</p>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-page">
        <Container className="text-center py-5">
          <h1 className="subscription-title">Choose Your Perfect Plan</h1>
          <p className="subscription-subtitle">Error loading plans. Please try again later.</p>
        </Container>
      </div>
    );
  }

  return (
    <div className="subscription-page new-subscription-page">
      {/* Header Section */}
      <div className="new-subscription-header">
        <div className="header-content">
        <div className="logo-section">
          <img src={LogoSvg} alt="Complimate Logo" style={{ height: 50, verticalAlign: 'middle' }} />
        </div>
          <div className="user-profile-section">
            <div 
              className="user-avatar"
              style={{
                backgroundImage: userProfilePic ? `url(${getProfilePicUrl(userProfilePic)})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                width: '33px',
                height: '33px',
                borderRadius: '50%',
                marginRight: '8px',
                flexShrink: 0,
                backgroundColor: !userProfilePic ? '#009391' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {!userProfilePic && (
                <span style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>
                  {userFullName ? userFullName.charAt(0).toUpperCase() : 'U'}
                </span>
              )}
            </div>
            <div className="user-name">{userFullName || 'User'}</div>
            <div className="user-actions">
              <div style={{ position: 'relative' }}>
                <button 
                  className="header-icon-btn"
                  data-options-button
                  onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: '33px',
                    height: '33px',
                    border: 'none',
                    background: '#009391',
                    borderRadius: '13px',
                    cursor: 'pointer',
                    padding: 0,
                    margin: 0
                  }}
                >
                  <MoreVertIcon style={{ 
                    fontSize: '23px',
                    color: '#fff',
                  }} />
                </button>

                {/* Options Menu Dropdown */}
                {showOptionsMenu && (
                  <div 
                    data-options-menu
                    style={{
                      position: 'absolute',
                      top: '45px',
                      right: '0px',
                      backgroundColor: mode === 'dark' ? '#333' : '#fff',
                      border: `1px solid ${mode === 'dark' ? '#555' : '#ddd'}`,
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '8px 0',
                      minWidth: '160px',
                      zIndex: 1000
                    }}
                  >
                    <button
                      onClick={handleLogout}
                      style={{
                        width: '100%',
                        padding: '8px 16px',
                        border: 'none',
                        background: 'transparent',
                        color: mode === 'dark' ? '#fff' : '#333',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = mode === 'dark' ? '#555' : '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Container fluid className="subscription-container">
        {/* Main Title Section */}
        <div className="subscription-header text-center">
          <h1 className="subscription-title">Choose Your Perfect Plan</h1>
          <p className="subscription-subtitle">
            Pick a plan that fits your goals and unlock premium features anytime.
          </p>
        </div>

        {/* Expired Subscription Section */}
        {expiredSubscription && (
          <div className="expired-subscription-section">
            <div className="expired-subscription-card">
              <div className="expired-subscription-content">
                <div className="expired-subscription-left">
                  <h2 className="expired-subscription-title">Your Current Plan</h2>
                  <div className="expired-plan-details">
                    <h3 className="expired-plan-name">{expiredSubscription.plan_title}</h3>
                    <span className="expired-plan-status">Expired</span>
                  </div>
                  <p className="expired-plan-description">{expiredSubscription.subscription_plan_description}</p>
                  <div className="expired-plan-pricing">
                    <span className="expired-plan-amount">${expiredSubscription.amount_paid}</span>
                    <span className="expired-plan-cycle">/{expiredSubscription.billing_cycle}</span>
                  </div>
                </div>
                <div className="expired-subscription-right">
                  <div className="expired-subscription-due-date">
                    <span className="due-date-label">Plan Expired on:</span>
                    <span className="due-date-value">{new Date(expiredSubscription.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Free Plan Section */}
        {freePlan && !expiredSubscription && (
          <div className="free-plan-section">
            <div className="free-plan-card">
              <div className="free-plan-content">
                <div className="free-plan-left">
                  <h2 className="free-plan-title">{freePlan.plan.title}</h2>
                  <p className="free-plan-description">
                    {getPlanDescription(freePlan.formatted_description)}
                  </p>
                </div>
                <div className="free-plan-right">
                  <ul className="free-plan-features">
                    {formatFeatures(freePlan.formatted_description)}
                  </ul>
                </div>
                <div className="free-plan-button-wrapper">
                  <button 
                    className="free-plan-button"
                    onClick={() => handlePlanSelection(freePlan.plan, freePlan.formatted_description)}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Start A Free Trial'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Other Paid Plans Section */}
        <div className="paid-plans-section">
          <div className="paid-plans-header">
            <h2 className="paid-plans-title">Other Paid Plans</h2>
            <div className="billing-toggle">
              <Button
                variant={billingToggle === 'MONTHLY' ? 'primary' : 'outline-secondary'}
                className={`toggle-btn ${billingToggle === 'MONTHLY' ? 'active' : ''}`}
                onClick={() => setBillingToggle('MONTHLY')}
              >
                MONTHLY
              </Button>
              <Button
                variant={billingToggle === 'YEARLY' ? 'primary' : 'outline-secondary'}
                className={`toggle-btn ${billingToggle === 'YEARLY' ? 'active' : ''}`}
                onClick={() => setBillingToggle('YEARLY')}
              >
                YEARLY
              </Button>
            </div>
          </div>

          {/* Paid Plans Grid - Copied from SubscriptionPlans.js */}
          <Row className="plans-row">
            {sortPlans(paidPlans).map((planData, index) => {
              const { plan, formatted_description } = planData;
              const isPopular = isMostPopular(plan, index, sortPlans(paidPlans).length);
              const priceInfo = formatPrice(plan);
              const features = formatFeatures(formatted_description);
              const description = getPlanDescription(formatted_description);
              const enterprisePlan = isEnterprisePlan(plan);
              
              return (
                <Col key={plan.id} className={`plan-col ${isPopular ? 'popular-column' : ''}`}>
                  {isPopular ? (
                    <div className="popular-wrapper">
                      <div className="most-popular-badge">
                        Most Popular
                      </div>
                      <div className="plan-card">
                        <div className="plan-content">
                          {/* Plan Icon */}
                          <div className="plan-icon-container">
                            {getPlanIcon(plan.title)}
                          </div>

                          {/* Plan Title */}
                          <h3 className="plan-title">{plan.title} Plan</h3>
                          
                          {/* Plan Description */}
                          <p className="plan-description">{description}</p>

                          {/* Plan Pricing */}
                          <div className="plan-pricing">
                            {priceInfo.oldPrice && (
                              <span className="old-price">{priceInfo.oldPrice}</span>
                            )}
                            <div className="price-main">
                              <span className="new-price">{priceInfo.newPrice}</span>
                              <span className="price-period">{priceInfo.period}</span>
                            </div>
                          </div>

                          {/* Plan Features */}
                          <ul className="plan-features">
                            {features}
                          </ul>
                          
                          {/* Get Started Button */}
                          <div className="plan-button-container">
                            <button 
                              className="plan-button"
                              onClick={() => handlePlanSelection(plan, formatted_description)}
                            >
                              {enterprisePlan ? 'Contact Sales' : (plan.month_price ? 'Get Started' : 'Start Free Trial')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="plan-card">
                      <div className="plan-content">
                        {/* Plan Icon */}
                        <div className="plan-icon-container">
                          {getPlanIcon(plan.title)}
                        </div>

                        {/* Plan Title */}
                        <h3 className="plan-title">{plan.title} Plan</h3>
                        
                        {/* Plan Description */}
                        <p className="plan-description">{description}</p>

                        {/* Plan Pricing */}
                        <div className="plan-pricing">
                          {priceInfo.oldPrice && (
                            <span className="old-price">{priceInfo.oldPrice}</span>
                          )}
                          <div className="price-main">
                            <span className="new-price">{priceInfo.newPrice}</span>
                            <span className="price-period">{priceInfo.period}</span>
                          </div>
                        </div>

                        {/* Plan Features */}
                        <ul className="plan-features">
                          {features}
                        </ul>
                        
                        {/* Get Started Button */}
                        <div className="plan-button-container">
                          <button 
                            className="plan-button"
                            onClick={() => handlePlanSelection(plan, formatted_description)}
                          >
                            {enterprisePlan ? 'Contact Sales' : (plan.month_price ? 'Get Started' : 'Start Free Trial')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </Col>
              );
            })}
          </Row>
        </div>
      </Container>

      {/* Confirmation Dialog for Trial Plan */}
      <AlertDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleTrialConfirmation}
        type="warning"
        title="Start Free Trial"
        message={`Are you sure you want to start with the ${selectedPlan?.plan?.title || 'Free'} plan? This will activate your free trial and redirect you to the dashboard.`}
        confirmText="Yes, Start Trial"
        cancelText="Cancel"
        size="medium"
      />
    </div>
  );
}

export default NewSubscription;
